/**
 * The panel beside a note that opens when an internal link is clicked, so the note stays in view:
 * for a paper, the quoted text on the cited page (the PDF opens in a window only if needed); for a
 * note, its text; for a task or file, a short summary. Links inside the panel open in the panel.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, BookOpen, CircleCheck, ExternalLink, FileText, ListTodo, Lock, Paperclip, X } from "lucide-react";
import { parseInternalHref, type InternalLink } from "@brainlog/shared";
import { get, post } from "@/lib/api";
import { openNote, openPaper, openView } from "@/lib/store";
import { useNote } from "@/lib/queries";
import type { ResolvedRef, Task } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { FilePreviewDialog, canPreview } from "@/features/files/FilePreviewDialog";
import { downloadFile } from "@/features/files/download";
import { displayTitle, formatAuthorsShort, primaryPdf } from "@/features/library/format";
import { paperKey } from "@/features/library/useLibrary";
import type { PaperDetail } from "@/features/library/types";
import { MarkdownPreview } from "./MarkdownPreview";
import { kindLabel } from "./kinds";
import { LinkOpenerContext } from "./linkOpener";
import { scrollWithin } from "@/lib/scroll";

export interface OpenedLink {
  href: string;
  /** The text around the link in the note (its list item, paragraph or quotation). */
  context: string;
}

/** The quotation a link supports: “…” in the same item, or the lines of a block quotation above "— p. 3". */
export function quoteFromContext(context: string) {
  const unescape = (s: string) => s.replace(/\\([\\`*_[\]<>!])/g, "$1").trim();
  const curly = /“([^”]{12,})”/.exec(context)?.[1];
  if (curly) return unescape(curly);
  const straight = /"([^"\n]{12,})"/.exec(context)?.[1];
  if (straight) return unescape(straight);
  const lines = context
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length > 1 && /^[—–-]\s/.test(lines.at(-1)!)) return unescape(lines.slice(0, -1).join(" "));
  return null;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Where the quotation is in the page text (spacing, line breaks and case may differ). */
function findQuote(text: string, quote: string): [number, number] | null {
  const words = quote.split(/\s+/).filter(Boolean);
  for (const n of [words.length, Math.min(words.length, 10), Math.min(words.length, 6)]) {
    if (n < 3) break;
    const m = new RegExp(words.slice(0, n).map(escapeRe).join("[\\s\\-]+"), "i").exec(text);
    if (m) {
      // The whole quotation when it matched in full; otherwise up to the end of that sentence.
      if (n === words.length) return [m.index, m.index + m[0].length];
      const rest = text.slice(m.index + m[0].length);
      const stop = rest.search(/[.!?](\s|$)/);
      return [m.index, m.index + m[0].length + (stop >= 0 ? stop + 1 : 0)];
    }
  }
  return null;
}

export function LinkPanel({ stack, onBack, onClose, onOpen }: { stack: OpenedLink[]; onBack(): void; onClose(): void; onOpen(href: string, context: string): void }) {
  const current = stack.at(-1)!;
  const link = parseInternalHref(current.href);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollTo({ top: 0 });
  }, [current.href]);
  const what = !link ? "Link" : link.type === "note" ? "Note" : link.type === "task" ? "Task" : link.type === "file" ? "File" : "Evidence";
  return (
    <LinkOpenerContext.Provider value={onOpen}>
      <div className="flex h-full min-h-0 flex-col" onKeyDown={(e) => e.key === "Escape" && onClose()}>
        <div className="flex h-10 shrink-0 items-center gap-1 border-b px-2">
          {stack.length > 1 && (
            <Button size="icon-sm" variant="ghost" onClick={onBack} aria-label="Back">
              <ArrowLeft />
            </Button>
          )}
          <span className="flex-1 px-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">{what}</span>
          {link && <OpenInTab link={link} />}
          <Button size="icon-sm" variant="ghost" onClick={onClose} aria-label="Close the side panel">
            <X />
          </Button>
        </div>
        <div ref={ref} className="min-h-0 flex-1 overflow-y-auto px-5 py-4" role="region" aria-label={`${what} beside the note`}>
          {!link ? (
            <p className="text-sm text-muted-foreground">This link is not valid.</p>
          ) : link.type === "paper" || link.type === "cite" ? (
            <PaperEvidence key={current.href} link={link} context={current.context} />
          ) : link.type === "note" ? (
            <NotePeek key={link.id} id={link.id} />
          ) : link.type === "task" ? (
            <TaskPeek id={link.id} />
          ) : link.type === "file" ? (
            <FilePeek id={link.id} />
          ) : (
            <p className="text-sm text-muted-foreground">Open this item in a tab.</p>
          )}
        </div>
      </div>
    </LinkOpenerContext.Provider>
  );
}

function OpenInTab({ link }: { link: InternalLink }) {
  const resolved = useResolved(link);
  const r = resolved.data;
  const open = () => {
    const title = r?.title ?? "Item";
    if (link.type === "note") openNote(link.id, title, r?.workspaceId);
    else if (link.type === "paper" || link.type === "cite") openPaper(r?.redirectedTo ?? link.id, title, link.page, r?.workspaceId);
    else if (link.type === "task") openView("backlog", r?.workspaceId, "Backlog", { task: link.id });
  };
  if (link.type === "file") return null;
  return (
    <Button size="sm" variant="ghost" onClick={open} disabled={!r?.accessible} className="gap-1 text-xs">
      <ExternalLink /> Open in a tab
    </Button>
  );
}

function useResolved(link: InternalLink) {
  const key = `${link.type === "cite" ? "paper" : link.type}:${link.id}`;
  return useQuery({
    queryKey: ["resolve", key],
    queryFn: async () => (await get<{ resolved: Record<string, ResolvedRef> }>(`/api/resolve?refs=${encodeURIComponent(key)}`)).resolved[key] ?? null,
    staleTime: 10_000,
  });
}

const Unavailable = () => <p className="text-sm text-muted-foreground">This item does not exist, or you do not have access to it.</p>;

/** A paper link: the quoted text on the cited page, the page text around it, and the PDF in a window. */
function PaperEvidence({ link, context }: { link: InternalLink; context: string }) {
  const detail = useQuery({ queryKey: paperKey(link.id), queryFn: () => get<PaperDetail>(`/api/library/${link.id}`), retry: false });
  const pdf = primaryPdf(detail.data?.attachments);
  const quote = quoteFromContext(context);
  // No quotation in the text: find the passage of the paper that best matches the cited sentence.
  const claim = context.replace(/\s+/g, " ").trim();
  const match = useQuery({
    queryKey: ["evidence", link.id, link.page ?? null, claim],
    queryFn: () =>
      post<{ available: boolean; reason: string | null; matches: { sentence: string; page: number | null; pageLabel: string | null; overlap: number; from: "pdf" | "abstract" }[] }>(`/api/library/${link.id}/evidence`, {
        claim,
        page: link.page ?? null,
      }),
    enabled: !quote && claim.length >= 10,
    retry: false,
    staleTime: 60_000,
  });
  const best = match.data?.matches[0] ?? null;
  const page = link.page ?? best?.page ?? undefined;
  const pageText = useQuery({
    queryKey: ["attachment-page", pdf?.id, page],
    queryFn: () => get<{ page: { pageNumber: number; pageLabel: string | null; text: string } }>(`/api/attachments/${pdf!.id}/pages/${page}`),
    enabled: !!pdf && !!page && !pdf.encrypted,
    retry: false,
  });
  const text = pageText.data?.page.text ?? "";
  const sought = quote ?? (best?.from === "pdf" ? best.sentence : null);
  const at = useMemo(() => (sought && text ? findQuote(text, sought) : null), [sought, text]);
  const [pdfOpen, setPdfOpen] = useState(false);
  const markRef = useRef<HTMLElement>(null);
  useEffect(() => {
    scrollWithin(markRef.current, { block: "center", behavior: "auto" });
  }, [at]);

  if (detail.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!detail.data) return <Unavailable />;
  const item = detail.data.item;
  const label = link.pageLabel ?? pageText.data?.page.pageLabel ?? null;
  const where = page ? `PDF page ${page}${label && label !== String(page) ? ` (printed page ${label})` : ""}` : null;

  return (
    <div className="space-y-4 text-sm">
      <div className="space-y-0.5">
        <h3 className="text-base leading-snug font-semibold">{displayTitle({ title: item.title, attachments: detail.data.attachments })}</h3>
        <p className="text-muted-foreground">
          {formatAuthorsShort(item.authors) || "Authors not recorded"} · {item.year ?? "n.d."}
          {where ? ` · ${where}` : ""}
        </p>
      </div>

      {quote ? (
        <figure className="space-y-1.5">
          <blockquote className="rounded-md border-l-4 border-warning bg-warning/10 px-3 py-2 font-serif text-[0.98rem] leading-relaxed">“{quote}”</blockquote>
          {page && text && (
            <figcaption className="flex items-center gap-1 text-xs text-muted-foreground">
              {at ? (
                <>
                  <CircleCheck className="size-3.5 text-success" aria-hidden /> Found on {where}.
                </>
              ) : (
                <>Not found word for word in the extracted text of this page (formulas, tables and columns can change the text). Check it in the PDF.</>
              )}
            </figcaption>
          )}
        </figure>
      ) : context ? (
        <div className="space-y-3">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">In your text</p>
            <p className="rounded-md bg-muted px-3 py-2 font-serif leading-relaxed">{context}</p>
          </div>
          {match.isLoading ? (
            <p className="text-xs text-muted-foreground">Looking for the passage of the paper that supports it…</p>
          ) : best ? (
            <figure className="space-y-1.5">
              <p className="text-xs text-muted-foreground">Best matching passage in the paper</p>
              <blockquote className="rounded-md border-l-4 border-warning bg-warning/10 px-3 py-2 font-serif text-[0.98rem] leading-relaxed">“{best.sentence}”</blockquote>
              <figcaption className="text-xs text-muted-foreground">
                {best.from === "abstract" ? "From the abstract" : `PDF page ${best.page}${best.pageLabel && best.pageLabel !== String(best.page) ? ` (printed ${best.pageLabel})` : ""}`} ·{" "}
                {Math.round(best.overlap * 100)}% of the sentence's key words. A match of words, not of meaning: read it before relying on it.
              </figcaption>
            </figure>
          ) : match.data ? (
            <p className="text-xs text-muted-foreground">{match.data.reason ?? "No passage of the paper closely matches this sentence. Check the citation."}</p>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {pdf && (
          <Button size="sm" variant="outline" onClick={() => setPdfOpen(true)}>
            <FileText /> {page ? `Open the PDF at page ${page}` : "Open the PDF"}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => openPaper(item.id, item.title || "Paper", page, item.workspaceId)}>
          <BookOpen /> Paper page
        </Button>
      </div>

      {page && pdf && !pdf.encrypted && (
        <details open={!quote || !at} className="group">
          <summary className="cursor-pointer text-xs font-medium text-muted-foreground">Text of {where}</summary>
          <div className="mt-2 max-h-[50vh] overflow-y-auto rounded-md border bg-card p-3 font-serif text-[0.92rem] leading-relaxed whitespace-pre-wrap">
            {pageText.isLoading ? (
              <span className="text-muted-foreground">Loading the page text…</span>
            ) : !text.trim() ? (
              <span className="text-muted-foreground">No extracted text for this page. Open the PDF to read it.</span>
            ) : at ? (
              <>
                {text.slice(0, at[0])}
                <mark ref={markRef} className="evidence-mark">
                  {text.slice(at[0], at[1])}
                </mark>
                {text.slice(at[1])}
              </>
            ) : (
              text
            )}
          </div>
        </details>
      )}
      {!page && item.abstract && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Abstract</p>
          <p className="font-serif leading-relaxed">{item.abstract}</p>
        </div>
      )}
      {!pdf && <p className="text-xs text-muted-foreground">No PDF is attached to this paper. Add it on the paper page to see the cited page.</p>}
      {pdfOpen && pdf && (
        <FilePreviewDialog file={{ ...pdf, encrypted: !!pdf.encrypted }} title={item.title || "Paper"} wide initialPage={page} onClose={() => setPdfOpen(false)} />
      )}
    </div>
  );
}

function NotePeek({ id }: { id: string }) {
  const note = useNote(id);
  if (note.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!note.data) return <Unavailable />;
  const n = note.data.note;
  if (n.encryptionScopeId)
    return (
      <p className="flex items-start gap-2 text-sm text-muted-foreground">
        <Lock className="mt-0.5 size-4 shrink-0" aria-hidden /> This note is end-to-end encrypted. Open it in a tab to unlock and read it.
      </p>
    );
  return (
    <div className="space-y-3">
      <div>
        <h3 className="font-display text-xl leading-tight font-semibold">{n.title}</h3>
        {n.kind !== "note" && <p className="text-xs text-muted-foreground">{kindLabel(n.kind)}</p>}
      </div>
      <MarkdownPreview markdown={n.contentMd} className="text-[0.98rem]" />
    </div>
  );
}

function TaskPeek({ id }: { id: string }) {
  const q = useQuery({ queryKey: ["task", id], queryFn: () => get<{ task: Task }>(`/api/tasks/${id}`), retry: false });
  if (q.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!q.data) return <Unavailable />;
  const t = q.data.task;
  const status = { backlog: "Backlog", todo: "To do", in_progress: "In progress", done: "Done" }[t.status];
  return (
    <div className="space-y-2 text-sm">
      <h3 className="flex items-start gap-2 text-base font-semibold">
        <ListTodo className="mt-0.5 size-4 shrink-0" aria-hidden /> {t.title}
      </h3>
      <p className="text-muted-foreground">
        {status}
        {t.dueDate ? ` · due ${t.dueDate}` : ""}
        {t.assigneeName ? ` · ${t.assigneeName}` : ""}
        {t.workspaceName ? ` · ${t.workspaceName}` : ""}
      </p>
      {t.description && <MarkdownPreview markdown={t.description} className="text-[0.95rem]" />}
    </div>
  );
}

function FilePeek({ id }: { id: string }) {
  const resolved = useResolved({ type: "file", id });
  const [open, setOpen] = useState(false);
  const r = resolved.data;
  if (resolved.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!r?.accessible) return <Unavailable />;
  const file = { id, filename: r.title ?? "file", mime: r.mime ?? "application/octet-stream", sizeBytes: r.sizeBytes ?? 0, sha256: r.sha256 ?? "", encrypted: !!r.encrypted };
  return (
    <div className="space-y-3 text-sm">
      <h3 className="flex items-center gap-2 font-semibold">
        <Paperclip className="size-4" aria-hidden /> {file.filename}
      </h3>
      <div className="flex gap-2">
        {canPreview(file) && (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            <FileText /> Preview
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => void downloadFile(file)}>
          Download
        </Button>
      </div>
      {open && <FilePreviewDialog file={file} onClose={() => setOpen(false)} wide />}
    </div>
  );
}
